import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Checkbox from 'components/Form/Checkbox';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import Information from 'components/Information';
import Layout from 'components/Layout';
import LinkButton from 'components/LinkButton';
import {FormFields} from 'types/utils';
import {onApiFormError} from 'utils/request/result';
import {PostDeletePreviewResponse, usePostDeletePreview} from '../services';
import {DeletePartner, DeletionSubmissionFormData, UserAccount} from '../types';

export type DeletionFormProps = {
    deletePartners: Array<DeletePartner>;
    hasMultiplePartners: boolean;
    hasReasons: boolean;
    onSuccess: (data: {
        formData: DeletionSubmissionFormData;
        previewData: PostDeletePreviewResponse;
    }) => void;
    userAccount: UserAccount;
};

const HomeDeletionForm: FC<DeletionFormProps> = ({
    deletePartners,
    hasMultiplePartners,
    hasReasons,
    onSuccess,
    userAccount,
}) => {
    const defaultValues = useMemo<DeletionSubmissionFormData>(() => {
        const ids = deletePartners.map(
            (account) => `delete_partner_${account.mid}`,
        );

        return {
            delete_user: false,
            improvements: '',
            reason_detail: '',
            selected_reason: undefined,
            ...Object.fromEntries(ids.map((id) => [id, false])),
        };
    }, [deletePartners]);

    const methods = useForm<FormFields<DeletionSubmissionFormData>>({
        defaultValues,
        shouldUnregister: true,
    });
    const {clearErrors, getValues, handleSubmit, setValue, watch} = methods;

    watch();

    const [errorMessage, setErrorMessage] = useState<string>();

    const {mutateAsync} = usePostDeletePreview();

    const onDeleteUser = useCallback(
        (value: boolean) => {
            const deletePartnerIds = deletePartners.map(
                (account) =>
                    `delete_partner_${account.mid}` as `delete_partner_${string}`,
            );
            deletePartnerIds.forEach((account) => {
                setValue(account, value);
            });
        },
        [deletePartners, setValue],
    );

    const deleteUser = getValues('delete_user');

    useEffect(() => {
        onDeleteUser(deleteUser);
    }, [deleteUser, onDeleteUser]);

    const {t} = useTranslation('Profile.DeletionConfirm.DeletionForm');

    const onSubmit = async (formData: DeletionSubmissionFormData) => {
        const deletePartnerIds = Object.fromEntries(
            Object.entries(formData).filter(([key]) =>
                key.includes('delete_partner'),
            ),
        );

        if (
            !hasMultiplePartners &&
            deletePartners.length > 0 &&
            !formData.delete_user &&
            !Object.values(deletePartnerIds).includes(false)
        ) {
            return setErrorMessage(t('errorAtLeastOneCompany'));
        }

        if (
            formData.delete_user &&
            Object.values(deletePartnerIds).includes(false)
        ) {
            return setErrorMessage(t('errorYouWantToDeleteYourAccount'));
        }

        if (
            !formData.delete_user &&
            !Object.values(deletePartnerIds).includes(true)
        ) {
            return setErrorMessage(t('errorYouDidNotSelectAnything'));
        }

        setErrorMessage(undefined);
        clearErrors();

        await mutateAsync(formData)
            .then((response) => {
                if (response) {
                    onSuccess({
                        formData,
                        previewData: response.data,
                    });
                }
            })
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Layout.Stacked className="max-w-xl">
                    <strong>{t('areYouSure')}</strong>
                    <ul>
                        <li>
                            <a href="/billing/details/">
                                {t('billingDetails')}
                            </a>
                        </li>
                        <li>
                            <a href="/notification/">
                                {t('notificationSettings')}
                            </a>
                        </li>
                    </ul>
                    <div className="flex">
                        <strong>{t('selectTheData')}</strong>
                    </div>
                    <Field label={t('userAccount')} type="checkbox">
                        <Checkbox
                            aria-label="User Account"
                            label={userAccount.name}
                            name="delete_user"
                        />
                    </Field>
                    {deletePartners.length > 0 && (
                        <>
                            <Information aria-label="user-warning" type="alert">
                                {t('userAccountDeletionWarning')}
                            </Information>
                            <Field label={t('companies')} type="checkbox">
                                <fieldset disabled={deleteUser}>
                                    <CheckboxRadioGroup>
                                        {deletePartners.map((account) => (
                                            <Checkbox
                                                key={account.mid}
                                                aria-label={`partner-${account.mid}`}
                                                label={
                                                    <>
                                                        {account.name}{' '}
                                                        <a
                                                            href={`/co/${account.mid}/team/change-owner/`}
                                                        >
                                                            {t('changeOwner')}
                                                        </a>
                                                    </>
                                                }
                                                name={`delete_partner_${account.mid}`}
                                            />
                                        ))}
                                    </CheckboxRadioGroup>
                                </fieldset>
                            </Field>
                        </>
                    )}
                    {errorMessage && (
                        <Information
                            aria-label="companies-warning"
                            type="danger"
                        >
                            {errorMessage}
                        </Information>
                    )}
                    {hasReasons && (
                        <>
                            <Radio
                                label={t('selectedReasonLabel')}
                                name="selected_reason"
                                options={[
                                    {
                                        label: t('selectedReasonOption1'),
                                        value: 'no_need_for_doc_management',
                                    },
                                    {
                                        label: t('selectedReasonOption2'),
                                        value: 'switched_to_competitor',
                                    },
                                    {
                                        label: t('selectedReasonOption3'),
                                        value: 'lack_of_feature',
                                    },
                                    {
                                        label: t('selectedReasonOption4'),
                                        value: 'bad_user_experience',
                                    },
                                    {
                                        label: t('selectedReasonOption5'),
                                        value: 'expensive',
                                    },
                                    {
                                        label: t('selectedReasonOption6'),
                                        value: 'other',
                                    },
                                ]}
                                required={t('form:required')}
                            />
                            <TextArea
                                aria-label="reason_detail"
                                label={t('reasonDetailLabel')}
                                name="reason_detail"
                                required={t('form:required')}
                                rows={4}
                            />
                        </>
                    )}
                    <TextArea
                        aria-label="improvements"
                        label={t('improvementsLabel')}
                        name="improvements"
                        rows={4}
                    />
                    <FormResult />
                    <div className="flex justify-end">
                        <LinkButton className="mr-8" href="/" kind="tertiary">
                            {t('form:cancel')}
                        </LinkButton>
                        <SubmitButton kind="destructive">
                            {t('preview')}
                        </SubmitButton>
                    </div>
                </Layout.Stacked>
            </form>
        </FormProvider>
    );
};

export default HomeDeletionForm;
